import React, { useCallback, useEffect, useState } from 'react'
import { Container, Button, Row, Col, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'
import TablePaginator from './common/TablePaginator'

import DailyRunForm from './daily_run/DailyRunForm'
import DailyRunList from './daily_run/DailyRunList'
import { SIMPLE_CURRENT_DAILY_RUNS_URL, SIMPLE_PREVIOUS_DAILY_RUNS_URL, TRUCKS_URL, DAILY_RUNS_URL } from './api_urls'
import configuredAxios from '../configured-axios'
import { dmyString } from '../helper'
import { useHistory } from 'react-router-dom'

function RunPage() {
  const history = useHistory()
  const [currentDailyRuns, setCurrentDailyRuns] = useState([])
  const [previousDailyRuns, setPreviousDailyRuns] = useState([])
  const [trucks, setTrucks] = useState([])
  const [dailyRunFormErrors, setDailyRunFormErrors] = useState({})
  const currentDateString = DateTime.now().toISODate()
  const emptyDailyRun = {
    date: currentDateString,
    truck: null,
    code: ''
  }
  const [newDailyRun, setNewDailyRun] = useState(emptyDailyRun)

  const [totalPreviousDailyRuns, setTotalPreviousDailyRuns] = useState(0)
  const [previousDailyRunsPerPage, setPreviousDailyRunsPerPage] = useState(20)
  const [previousDailyRunsOffest, setPreviousDailyRunsOffset] = useState(0)

  const refreshPreviousDailyRuns = useCallback(() => {
    const queryParams = new URLSearchParams({ limit: previousDailyRunsPerPage, offset: previousDailyRunsOffest })
    configuredAxios.get(SIMPLE_PREVIOUS_DAILY_RUNS_URL + "?" + queryParams.toString()).then((res) => {
      setPreviousDailyRuns(res.data.runs)
      setTotalPreviousDailyRuns(res.data.totalCount)
    })
  }, [previousDailyRunsPerPage, previousDailyRunsOffest])

  const refreshCurrentDailyRuns = useCallback(() => {
    configuredAxios.get(SIMPLE_CURRENT_DAILY_RUNS_URL).then((res) => {
      setCurrentDailyRuns(res.data)
    }).catch((err) => {
      toast.error('Could not retrieve daily runs')
      console.log(err)
    })
  }, [])

  // Get current daily runs on load
  useEffect(() => {
    refreshCurrentDailyRuns()
    refreshPreviousDailyRuns()
  }, [refreshCurrentDailyRuns, refreshPreviousDailyRuns])

  // Get Truck list when daily run date changes
  useEffect(() => {
    if (newDailyRun.date) {
      const searchParams = new URLSearchParams({
        date: newDailyRun.date,
        operator: 'NotEqual',
        status: 'true'
      })
      configuredAxios.get(TRUCKS_URL, { params: searchParams }).then((res) => {
        setTrucks(res.data)
      }).catch((err) => {
        toast.error('Could not retrieve Trucks')
        console.log(err)
      })
    } else {
      setTrucks([])
    }
  }, [newDailyRun.date])

  function validateNewDailyRun(run) {
    const errors = {}

    if (!run.date) errors.date = 'Date is required'
    if (!run.truckId) errors.truck = 'Truck is required'

    return errors
  }

  function handleSubmitNewDailyRun() {
    const dailyRunData = {
      date: newDailyRun.date,
      truckId: newDailyRun.truck?.value
    }
    const errors = validateNewDailyRun(dailyRunData)
    if (Object.keys(errors).length === 0) {
      configuredAxios.post(DAILY_RUNS_URL, dailyRunData).then((res) => {
        toast.success('Run Created Successfully')
        const run = res.data

        if (new Date(run.date) >= new Date(currentDateString)) {
          refreshCurrentDailyRuns()
        } else {
          refreshPreviousDailyRuns()
        }

        const newTrucks = trucks.slice().filter((truck) => {
          return truck.id !== run.truckId
        })

        setTrucks(newTrucks)
        setNewDailyRun(emptyDailyRun)
      }).catch((err) => {
        toast.error('Run could not be created')
        console.log(err)
      })
    } else {
      setDailyRunFormErrors(errors)
    }
  }

  function handleDailyRunChange(dailyRun) {
    setDailyRunFormErrors({})
    setNewDailyRun(dailyRun)
  }

  const previousDailyRunTableRows = []
  for (const dailyRun of previousDailyRuns) {
    const truckNumber = dailyRun.truck ? dailyRun.truck.number : ''
    const truckDescription = dailyRun.truck ? dailyRun.truck.description : ''
    previousDailyRunTableRows.push(
      <tr onClick={() => history.push('/run/' + dailyRun.id)} key={dailyRun.id}>
        <td>{dmyString(dailyRun.date)}</td>
        <td>{dailyRun.code}</td>
        <td>{truckNumber}</td>
        <td>{truckDescription}</td>
      </tr>
    )
  }

  return (
    <Container>
      <h2>Create a Daily Run</h2>
      <Container>
        <Row>
          <Col>
            <DailyRunForm
              truckList={trucks}
              onChange={(dailyRun) => { handleDailyRunChange(dailyRun) }}
              dailyRun={newDailyRun}
              errors={dailyRunFormErrors}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button variant='primary' onClick={handleSubmitNewDailyRun} className='float-right'>
              Submit
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <h4>Daily Runs</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            <DailyRunList dailyRuns={currentDailyRuns} />
          </Col>
        </Row>
        <Row>
          <Col>
            <h4>Previous Daily Runs</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <TablePaginator
                className='mb-2'
                onFirstPage={() => {
                  setPreviousDailyRunsOffset(0)
                }}
                onNextPage={() => {
                  setPreviousDailyRunsOffset(previousDailyRunsOffest + previousDailyRunsPerPage)
                }}
                onPreviousPage={() => {
                  let newOffset = previousDailyRunsOffest - previousDailyRunsPerPage
                  if (newOffset < 0) {
                    newOffset = 0
                  }
                  setPreviousDailyRunsOffset(previousDailyRunsOffest)
                }}
                onLastPage={() => {
                  setPreviousDailyRunsOffset(totalPreviousDailyRuns - previousDailyRunsOffest)
                }}
                onItemsPerPageSelect={(selection) => { setPreviousDailyRunsPerPage(selection) }}
                itemsPerPage={previousDailyRunsPerPage}
                totalItems={totalPreviousDailyRuns}
                offset={previousDailyRunsOffest}
              />
            </div>
            <Table bordered hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Code</th>
                  <th>Truck Number</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {previousDailyRunTableRows}
              </tbody>
            </Table>
            {/* <DailyRunList dailyRuns={previousDailyRuns} /> */}
          </Col>
        </Row>
      </Container>
    </Container>
  )
}

export default RunPage
